import React, { useMemo, useState, useEffect, useCallback, memo } from "react";

import * as SC from "./CampaignCreatePostEditor.styled";

import _ from "lodash";
import { CustomerSucessController } from "@talentbait/shared-components";
import { SwitchTransition, CSSTransition } from "react-transition-group";

import CampaignCreateChannelLinkEditor from "../Campaign__Create_ChannelLinkEditor";

import usePrevious from "../../../../hooks/usePrevious";
import Actions from "../../../../Actions";
import logger from "../../../../utils/LoggerFrontend";
import { getImageDimensions } from "../utils";
import PostEditorSwitch from "./post-editor-switch/PostEditorSwitch";
import { getMediaAssets } from "../componentUtils";
import { useCampaignContext } from "../../../../context/Campaign.context";

const CampaignCreatePostEditor = ({
  selectedPreviewChannel,
  mobile,
  selectedPostNumber,
  channelPosts,
  postData,
  channelData,
  isReleased,
  editable,
  userName,
  clientView,
  isPrintView = false,
  selectedCreativeIndexToEdit,
  setSelectedCreativeIndexToEdit,
  campaignId,
  adsetId,
  isExternal = false,
  orgaId,
  orgaLinkedInPageID,
  showAts,
  hasAts,
  showLeadForm,
  jobPageLink,
  selectedChannelCampaignData,
  showLinkEditor = false,
  campaignLanguage,
  companyId
}) => {
  logger.debug("CampaignCreatePostEditor:init", "called", {
    showAts,
    showLeadForm,
    selectedPreviewChannel,
    showLinkEditor,
    selectedPostNumber,
    campaignId,
    adsetId,
    orgaId,
    selectedChannelCampaignData,
    selectedCreativeIndexToEdit,
    channelData,
    companyId
  });

  const { selectedChannels } = useCampaignContext();
  const [shouldUseLongImage, setShouldUseLongImage] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({});
  logger.debug("CampaignCreatePostEditor", "called", {
    creatives: postData?.creatives,
    selectedCreativeIndexToEdit,
    selectedPreviewChannel,
    shouldUseLongImage
  });

  const getMediaAssetData = async (mediaAssetId) => {
    logger.log("CampaignCreatePostEditor:getMediaAssetData", "Getting a single media asset", {
      mediaAssetId
    });

    const mediaAssetData = await getMediaAssets([mediaAssetId]);
    if (mediaAssetData && mediaAssetData.length > 0) {
      return mediaAssetData[0].inputProps.backgroundImage;
    }

    return "";
  };

  useEffect(() => {
    const fetchDataAndSetDimensions = async () => {
      logger.debug("CampaignCreatePostEditor:getImageDimensions", "found dimensions of image", {
        postData
      });

      const processImageDimensions = async (mediaAsset) => {
        try {
          const { width, height, ar } = await getImageDimensions(mediaAsset);
          logger.debug("CampaignCreatePostEditor:getImageDimensions", "found dimensions of image", {
            campaignId,
            width,
            height,
            ar,
            selectedPreviewChannel,
            shouldUseLongImage
          });

          setImageDimensions({ width, height, ar });
          setShouldUseLongImage(parseFloat(ar).toFixed(2) === "0.56");
        } catch (error) {
          logger.log("error:CampaignCreatePostEditor", "unable to determine ar of image", {
            error,
            campaignId
          });
        }
      };

      if (postData.channel === "instagram") {
        if (postData.mediaAssets?.[selectedCreativeIndexToEdit]) {
          const mediaAsset = await getMediaAssetData(
            postData.mediaAssets[selectedCreativeIndexToEdit]
          );
          logger.debug("CampaignCreatePostEditor:getImageDimensions", "found dimensions of image", {
            mediaAsset
          });

          await processImageDimensions(mediaAsset);
        }
      } else {
        if (postData.creatives?.[selectedCreativeIndexToEdit]) {
          await processImageDimensions(postData.creatives[selectedCreativeIndexToEdit]);
        }
      }
    };

    fetchDataAndSetDimensions();
  }, [
    postData,
    selectedCreativeIndexToEdit,
    campaignId,
    selectedPreviewChannel,
    shouldUseLongImage
  ]);

  const handleUpdatePost = useCallback(
    (updateData) => {
      logger.log("CampaignCreatePostEditor:handleUpdatePost", "called", {
        selectedPreviewChannel,
        selectedPostNumber,
        campaignId,
        updateData,
        userName,
        isReleased
      });

      if (clientView && postData?.isAutoGenerated) {
        CustomerSucessController.send("Edited autogenerated Post", {
          campaignId,
          updateData
        });
      }

      Actions.updatePost({
        channelShortName: selectedPreviewChannel,
        postNumber: selectedPostNumber,
        isReleased,
        userName,
        updateData,
        campaignId
      });
    },
    [
      selectedPreviewChannel,
      selectedPostNumber,
      isReleased,
      userName,
      clientView,
      postData,
      campaignId
    ]
  );

  const handleUpdateChannelData = useCallback(
    ({ key, value, updateData }) => {
      logger.log("CampaignCreatePostEditor:handleUpdateChannelData", "called", {
        selectedPostNumber,
        campaignId,
        key,
        value
      });
      Actions.updateChannelData({
        channelShortName: selectedPreviewChannel,
        key,
        value,
        isReleased,
        userName,
        postNumber: selectedPostNumber,
        updateData,
        campaignId
      });
    },
    [selectedPreviewChannel, isReleased, userName, selectedPostNumber]
  );

  const platformAdId = useMemo(
    () =>
      selectedChannelCampaignData?.platformAdIds?.[
        selectedChannelCampaignData?.platformAdIds.length - 1
      ],
    [selectedChannelCampaignData]
  );

  const selectedPostId = useMemo(
    () => _.find(channelPosts, ({ postNumber }) => postNumber === selectedPostNumber)?.id,
    [channelPosts, selectedPostNumber]
  );

  // Trigger transition on channel, postNumber or mobile/web view change
  const transitionKey = useMemo(
    () => `${selectedPreviewChannel}_${mobile}_${selectedPostNumber}__${showAts}__${showLeadForm}`,
    [selectedPreviewChannel, mobile, selectedPostNumber, showAts, showLeadForm]
  );

  const [transitionDirection, setTransitionDirection] = useState("right");

  // Transition scroll direction based on postNumber change
  const prevPostNumber = usePrevious(selectedPostNumber);
  useEffect(() => {
    const selectedPostNumberIndex = channelPosts.findIndex(
      ({ postNumber }) => postNumber === selectedPostNumber
    );

    const prevPostNumberIndex = channelPosts.findIndex(
      ({ postNumber }) => postNumber === prevPostNumber
    );

    if (selectedPostNumberIndex > prevPostNumberIndex) {
      setTransitionDirection("left");
    } else if (selectedPostNumberIndex < prevPostNumberIndex) {
      setTransitionDirection("right");
    }
  }, [selectedPostNumber, prevPostNumber, channelPosts]);

  // Transition scroll direction based on mobile/web view change
  useEffect(() => {
    if (mobile) {
      setTransitionDirection("left");
    } else {
      setTransitionDirection("right");
    }
  }, [mobile]);

  // Transition scroll direction based on channel change
  const prevSelectedPreviewChannel = usePrevious(selectedPreviewChannel);
  const prevSelectedAts = usePrevious(showAts);
  const prevSelectedLeadForm = usePrevious(showLeadForm);
  useEffect(() => {
    const channelIndex = selectedChannels.findIndex(
      (channel) => channel === selectedPreviewChannel
    );
    const prevChannelIndex = selectedChannels.findIndex(
      (channel) => channel === prevSelectedPreviewChannel
    );
    // If showing job page and we just moved from another channel, transition lead form from the bottom
    if (showLeadForm && !prevSelectedAts) {
      setTransitionDirection("bottom");
    }
    // If showing job page and we just moved from qualification questions, transition lead form from the bottom
    else if (showLeadForm && prevSelectedAts) {
      setTransitionDirection("top");
    }
    // If showing channels and coming from job page then transition from the top
    if (!showAts && !showLeadForm && prevSelectedLeadForm) {
      setTransitionDirection("top");
    }
    // If showing qualification questions, always transition qualification from below towards the top
    else if (showAts) {
      setTransitionDirection("bottom");
    } else if (prevSelectedAts) {
      setTransitionDirection("top");
    } else if (channelIndex > prevChannelIndex) {
      setTransitionDirection("bottom");
    } else if (channelIndex < prevChannelIndex) {
      setTransitionDirection("top");
    }
  }, [
    selectedPreviewChannel,
    prevSelectedPreviewChannel,
    selectedChannels,
    showAts,
    prevSelectedAts,
    showLeadForm,
    prevSelectedLeadForm
  ]);

  const onClickPost = useCallback(() => {
    if (clientView && postData?.isAutoGenerated) {
      CustomerSucessController.send("Clicked autogenerated Post", {
        campaignId
      });
    }
  }, [campaignId, clientView, postData]);

  useEffect(() => {
    if (postData) {
      Actions.updateSelectedPostData(postData);
    }
  }, [postData]);

  logger.log("CampaignCreatePostEditor:render", "called", {
    selectedChannelCampaignData,
    selectedPreviewChannel,
    mobile,
    selectedPostNumber,
    channelPosts,
    selectedChannels,
    postData,
    channelData,
    isReleased,
    editable,
    userName,
    clientView,
    selectedCreativeIndexToEdit,
    setSelectedCreativeIndexToEdit,
    campaignId,
    adsetId,
    orgaId,
    showAts,
    showLeadForm,
    jobPageLink,
    showLinkEditor,
    campaignLanguage
  });

  return (
    <SC.PostEditorWrapper transitionDirection={transitionDirection}>
      <SwitchTransition>
        <CSSTransition
          key={transitionKey}
          addEndListener={(node, done) => node.addEventListener("transitionend", done, false)}
          classNames="fade"
        >
          <div>
            <div onClick={onClickPost}>
              <PostEditorSwitch
                selectedPostId={selectedPostId}
                selectedPreviewChannel={selectedPreviewChannel}
                mobile={mobile}
                postData={postData}
                channelData={channelData}
                updatePost={handleUpdatePost}
                updateChannelData={handleUpdateChannelData}
                editable={editable}
                clientView={clientView}
                isPrintView={isPrintView}
                selectedCreativeIndexToEdit={selectedCreativeIndexToEdit}
                setSelectedCreativeIndexToEdit={setSelectedCreativeIndexToEdit}
                orgaId={orgaId}
                orgaLinkedInPageID={orgaLinkedInPageID}
                adsetId={adsetId}
                campaignId={campaignId}
                companyId={companyId}
                showAts={showAts}
                showLeadForm={showLeadForm}
                jobPageLink={jobPageLink}
                selectedChannelCampaignData={selectedChannelCampaignData}
                shouldUseLongImage={shouldUseLongImage}
                imageDimensions={imageDimensions}
                hasAts={hasAts}
                campaignLanguage={campaignLanguage}
              />
            </div>

            {showLinkEditor && !showAts && !showLeadForm && !isExternal && (
              <CampaignCreateChannelLinkEditor
                isClientView={clientView}
                adsetId={adsetId}
                campaignId={campaignId}
                channelLink={channelData?.url}
                updateChannelData={handleUpdateChannelData}
                selectedPreviewChannel={selectedPreviewChannel}
                editable={editable}
                hasAts={hasAts}
                clientView={clientView}
                platformAdId={platformAdId}
                selectedPostId={selectedPostId}
                selectedPostNumber={selectedPostNumber}
              />
            )}
          </div>
        </CSSTransition>
      </SwitchTransition>
    </SC.PostEditorWrapper>
  );
};

export default memo(CampaignCreatePostEditor);
